/*
  <mono-cart-upsell> — the upsell module inside the cart drawer.

  Two jobs, both small:

  1. When data-source="auto", fetch sections/mono-cart-upsell.liquid through
     the product recommendations endpoint for the newest cart item and
     inject the returned list. The section filters out in-cart products
     server-side, so what arrives is already correct. An empty or failed
     response removes the element — no heading over blank space.

  2. Handle clicks on the add buttons (both sources) by POSTing to
     /cart/add with the drawer's own sections, then handing the parsed
     response to Dawn's cart-drawer element, whose renderContents() replaces
     the drawer markup and re-opens it. This element lives inside the
     markup being replaced, so a fresh instance takes over after every add —
     no state to clean up.

  Uses Dawn's own globals: routes.cart_add_url (layout/theme.liquid),
  fetchConfig (assets/global.js), and, when present, the pub/sub bus so the
  rest of the theme hears the cart change exactly as if a product form had
  fired it.
*/

class MonoCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    if (this.dataset.source === 'auto' && this.dataset.productId) {
      this.loadRecommendations();
    }
  }

  loadRecommendations() {
    const url = `${this.dataset.url}?section_id=mono-cart-upsell&product_id=${this.dataset.productId}&limit=${
      this.dataset.limit || 6
    }&intent=related`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const inner = html.querySelector('.mono-cart-upsell__inner');
        if (inner && inner.querySelector('.mono-cart-upsell__row')) {
          this.innerHTML = inner.outerHTML;
        } else {
          this.remove();
        }
      })
      .catch(() => {
        this.remove();
      });
  }

  onClick(event) {
    const button = event.target.closest('.mono-cart-upsell__add');
    if (!button || button.hasAttribute('aria-disabled')) return;
    event.preventDefault();

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('mono-cart-upsell__add--loading');
    const errorElement = this.querySelector('.mono-cart-upsell__error');
    if (errorElement) errorElement.hidden = true;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      id: Number(button.dataset.variantId),
      quantity: 1,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          // Shopify answered with an error (sold out, limit reached).
          button.removeAttribute('aria-disabled');
          button.classList.remove('mono-cart-upsell__add--loading');
          if (errorElement) errorElement.hidden = false;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mono-cart-upsell',
            productVariantId: Number(button.dataset.variantId),
            cartData: parsedState,
          });
        }

        const drawer = document.querySelector('cart-drawer');
        if (drawer && parsedState.sections) {
          drawer.renderContents(parsedState);
        }
      })
      .catch(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('mono-cart-upsell__add--loading');
        if (errorElement) errorElement.hidden = false;
      });
  }
}

customElements.define('mono-cart-upsell', MonoCartUpsell);
